import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Header from '../Header';
import api from '../services/apiService';
import { FaChevronDown, FaInfoCircle } from 'react-icons/fa';
import logoLong from '../images/logo-long.svg';
import { getMembershipSeasonDescription, getMembershipSeasonLabel, getSignonPhoneHref } from '../utils/signonContact';
import MembershipFeeModal from './MembershipFeeModal';
import '../scss/membershipParent.scss';

const londonYearMonth = () => {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London',
    year: 'numeric',
    month: '2-digit',
  }).formatToParts(new Date());
  const year = parts.find((part) => part.type === 'year')?.value;
  const month = parts.find((part) => part.type === 'month')?.value;
  return `${year}-${month}`;
};

const monthLabel = (yearMonth) => {
  const [year, month] = String(yearMonth).split('-').map(Number);
  if (!year || !month) {
    return yearMonth;
  }
  return new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString('en-GB', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

const deriveMissedMonths = (details) => {
  if (!details) {
    return [];
  }
  const listed = Array.isArray(details.missed_months) ? details.missed_months : null;
  if (listed && listed.length > 0) {
    return listed;
  }
  if (Array.isArray(details.season_months) && details.season_months.length > 0) {
    const fromSeason = details.season_months
      .filter((month) => month.past && !month.paid)
      .map((month) => ({
        year_month: month.year_month,
        name: month.name || monthLabel(month.year_month),
        amount_label: month.amount_label || '',
      }));
    if (fromSeason.length > 0) {
      return fromSeason;
    }
  }
  if (listed) {
    return listed;
  }
  const currentYm = details.current_year_month || londonYearMonth();
  if (Array.isArray(details.season_months) && details.season_months.length > 0) {
    return details.season_months
      .filter((month) => (month.year_month || '') < currentYm && !month.paid)
      .map((month) => ({
        year_month: month.year_month,
        name: month.name || monthLabel(month.year_month),
        amount_label: month.amount_label || (details.amount_label || '').replace(/\s+per month$/i, ''),
      }));
  }
  const startMonth = Number(details.season_start_month) || 8;
  const [year, month] = currentYm.split('-').map(Number);
  const startYear = month >= startMonth ? year : year - 1;
  const missed = [];
  let cursorYear = startYear;
  let cursorMonth = startMonth;
  const amountLabel = (details.amount_label || '').replace(/\s+per month$/i, '');
  while (`${cursorYear}-${String(cursorMonth).padStart(2, '0')}` < currentYm) {
    const yearMonth = `${cursorYear}-${String(cursorMonth).padStart(2, '0')}`;
    missed.push({
      year_month: yearMonth,
      name: monthLabel(yearMonth),
      amount_label: amountLabel,
    });
    cursorMonth += 1;
    if (cursorMonth > 12) {
      cursorMonth = 1;
      cursorYear += 1;
    }
  }
  return missed;
};

const fetchMembershipStatus = async (nextTeamId, nextPlayerId, fulfilSessionId) => {
  const body = { team_id: nextTeamId };
  if (nextPlayerId) {
    body.player_id = nextPlayerId;
  }
  if (fulfilSessionId) {
    body.fulfil_session_id = fulfilSessionId;
  }
  const response = fulfilSessionId
    ? await api.post('/get_membership_status.php', body)
    : await api.get(`/get_membership_status.php?${new URLSearchParams({
      team_id: String(nextTeamId),
      ...(nextPlayerId ? { player_id: String(nextPlayerId) } : {}),
    }).toString()}`);
  const data = response.data;
  if (!data?.success) {
    throw new Error(data?.message || 'Could not load subscription');
  }
  return data;
};

const childAmounts = (details) => {
  const isFree = Boolean(details?.is_free);
  const amountLabel = isFree
    ? 'Free'
    : details?.amount_label
      ? (/per month/i.test(details.amount_label) ? details.amount_label : `${details.amount_label} per month`)
      : '';
  const sibling = details?.sibling_discount || {};
  const discountApplied = !isFree && Boolean(sibling.applied && (details?.discount_label || sibling.discount_label));
  const discountPercent = Number(sibling.percent || details?.discount_percent || 0);
  const discountTitle = discountPercent > 0
    ? `Sibling discount (${Number.isInteger(discountPercent) ? discountPercent : discountPercent}%)`
    : 'Sibling discount';
  return { isFree, amountLabel, sibling, discountApplied, discountTitle };
};

const monthlyPence = (label) => {
  const match = String(label || '').replace(/,/g, '').match(/(\d+)(?:\.(\d{1,2}))?/);
  if (!match) {
    return 0;
  }
  return (Number(match[1]) * 100) + Number((match[2] || '0').padEnd(2, '0').slice(0, 2));
};

const gbp = (pence) => `£${Math.floor(pence / 100)}.${String(pence % 100).padStart(2, '0')}`;

const siblingOffer = (details) => {
  const amounts = childAmounts(details);
  if (!amounts.discountApplied) {
    return null;
  }
  const payPence = monthlyPence(amounts.amountLabel);
  const discountLabel = String(details?.discount_label || amounts.sibling.discount_label || '');
  const listPence = monthlyPence(details?.list_club_label || amounts.sibling.club_label);
  const discountPence = monthlyPence(discountLabel);
  let fullPence = listPence;
  if (!details?.fee_included && listPence > 0) {
    fullPence = listPence + Math.round(listPence * 5 / 100);
  }
  if (fullPence < 1) {
    fullPence = payPence + discountPence;
  }
  const percent = Number(amounts.sibling.percent || details?.discount_percent || 0);
  const percentText = Number.isInteger(percent) ? String(percent) : String(percent);
  const money = discountLabel.startsWith('£') ? discountLabel : gbp(discountPence);
  return {
    full: gbp(fullPence),
    pay: payPence > 0 ? gbp(payPence) : '',
    caption: `Sibling Discount: −${money} (${percentText}%)`,
  };
};

const MembershipParent = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [returnStatus] = useState(() => searchParams.get('status'));
  const [returnSessionId] = useState(() => searchParams.get('session_id'));
  const teamId = sessionStorage.getItem('selected_team_id');
  const playerId = sessionStorage.getItem('player_id');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [details, setDetails] = useState(null);
  const [family, setFamily] = useState([]);
  const [detailsOpen, setDetailsOpen] = useState(true);
  const [cancelOpen, setCancelOpen] = useState(true);
  const [acting, setActing] = useState('');
  const [portalError, setPortalError] = useState(null);
  const [reinstateError, setReinstateError] = useState(null);
  const [errorPlayerId, setErrorPlayerId] = useState(null);
  const [enteredEmail, setEnteredEmail] = useState('');
  const [emailError, setEmailError] = useState(null);
  const [paymentError, setPaymentError] = useState(null);
  const [feeChild, setFeeChild] = useState(null);

  const loadStatus = useCallback(async (options = {}) => {
    if (!teamId) {
      setError('No team selected');
      setIsLoading(false);
      return;
    }
    try {
      if (!options.quiet) {
        setIsLoading(true);
      }
      setError(null);
      const sessionPlayerId = playerId && playerId !== 'null' ? playerId : '';
      const sessionChildName = sessionStorage.getItem('child_name') || 'this child';
      if (!sessionPlayerId) {
        setDetails(null);
        setFamily([]);
        setError(`Choose ${sessionChildName}'s profile to see their subscription.`);
        return;
      }
      const fulfil = returnStatus === 'success' && returnSessionId && returnSessionId.startsWith('cs_')
        ? returnSessionId
        : '';
      const primary = await fetchMembershipStatus(teamId, sessionPlayerId, fulfil);
      if (String(primary.player_id) !== String(sessionPlayerId)) {
        setDetails(null);
        setFamily([]);
        setError(`Could not load ${sessionChildName}'s subscription.`);
        return;
      }
      setFamily([primary]);
      setDetails(primary);
    } catch (err) {
      const status = err.response?.status;
      if (!err.response || status === 404) {
        setError('Could not reach the subscription API. Upload get_membership_status.php to api.dev.');
      } else {
        setError(err.response?.data?.message || err.message || 'Could not load subscription');
      }
    } finally {
      setIsLoading(false);
    }
  }, [teamId, playerId, returnStatus, returnSessionId]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    if (returnStatus !== 'success') {
      return undefined;
    }
    const first = window.setTimeout(() => {
      loadStatus();
    }, 2000);
    const second = window.setTimeout(() => {
      loadStatus();
    }, 6000);
    return () => {
      window.clearTimeout(first);
      window.clearTimeout(second);
    };
  }, [returnStatus, loadStatus]);

  useEffect(() => {
    if (!searchParams.has('status') && !searchParams.has('session_id')) {
      return;
    }
    const next = new URLSearchParams(searchParams);
    next.delete('status');
    next.delete('session_id');
    next.delete('kind');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const contact = details?.contact || {};
  const hasContactEmail = Boolean(contact.email);
  const hasContactPhone = Boolean(contact.phone);
  const hasContact = hasContactEmail || hasContactPhone;
  const pageTitle = details?.page_title || 'Monthly Subscription';
  const seasonLabel = details?.season_label || getMembershipSeasonLabel();
  const seasonDescription = (details?.season_label && details.ticket_description)
    ? details.ticket_description
    : getMembershipSeasonDescription();
  const isFree = Boolean(details?.is_free);
  const amountLabel = isFree
    ? 'Free'
    : details?.amount_label
      ? (/per month/i.test(details.amount_label) ? details.amount_label : `${details.amount_label} per month`)
      : '';
  const offer = siblingOffer(details);
  const hasAccountEmail = Boolean(details?.has_email && details?.email);
  const setupReady = details?.setup_ready !== false;
  const missedMonths = deriveMissedMonths(details);
  const canPayMissed = setupReady && missedMonths.length > 0;
  const justPaid = returnStatus === 'success';
  const cancelling = Boolean(details?.cancel_at_period_end);
  const cancelled = ['cancelled', 'canceled', 'failed'].includes(details?.subscription_status)
    && !details?.subscribed;
  const confirmingPayment = justPaid && !details?.subscribed && !cancelling && !cancelled;
  const missingAccountEmail = details
    ? setupReady
      && !hasAccountEmail
      && (!details.subscribed || canPayMissed)
    : false;
  const cards = family.length > 0 ? family : (details ? [details] : []);
  const stacked = cards.length > 1;
  const childIds = (child) => ({
    team_id: child?.team_id || teamId,
    player_id: child?.player_id || (playerId && playerId !== 'null' ? playerId : undefined),
  });
  const starting = acting === `pay:${details?.player_id}`;
  const openingPortal = acting === `portal:${details?.player_id}`;
  const reinstating = acting === `reinstate:${details?.player_id}`;
  const busy = Boolean(acting);

  const saveEmailIfNeeded = async (child) => {
    const childSetup = child?.setup_ready !== false;
    const childHasEmail = Boolean(child?.has_email && child?.email);
    const childMissed = childSetup && deriveMissedMonths(child).length > 0;
    const needsEmail = child
      ? childSetup && !childHasEmail && (!child.subscribed || childMissed)
      : missingAccountEmail;
    if (!needsEmail) {
      return true;
    }
    const nextEmail = enteredEmail.trim();
    if (!nextEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(nextEmail)) {
      setEmailError('Enter a valid email address');
      return false;
    }
    const saved = await api.post('/update_email.php', { email: nextEmail });
    if (!saved.data?.success) {
      throw new Error(saved.data?.message || 'Could not save email');
    }
    const savedEmail = (saved.data.email || nextEmail).toLowerCase();
    const withEmail = (current) => (
      current
        ? {
            ...current,
            email: savedEmail,
            has_email: true,
            can_checkout: true,
          }
        : current
    );
    setDetails(withEmail);
    setFamily((current) => current.map(withEmail));
    return true;
  };

  const reveal = (selector) => {
    window.setTimeout(() => {
      document.querySelector(selector)?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, 0);
  };

  const startPayment = async (child = details) => {
    if (!child) {
      return;
    }
    const ids = childIds(child);
    try {
      setActing(`pay:${child.player_id}`);
      setEmailError(null);
      setPaymentError(null);
      if (!(await saveEmailIfNeeded(child))) {
        setActing('');
        reveal('.membership-parent-container .email-field');
        return;
      }
      const response = await api.post('/get_membership_status.php', {
        team_id: ids.team_id,
        player_id: ids.player_id,
        start_checkout: 1,
        app_origin: window.location.origin,
      });
      if (response.data?.checkout_url) {
        sessionStorage.setItem('selected_team_id', String(ids.team_id));
        if (ids.player_id) {
          sessionStorage.setItem('player_id', String(ids.player_id));
        }
        window.location.href = response.data.checkout_url;
        return;
      }
      throw new Error('Could not start payment');
    } catch (err) {
      const message = err.response?.data?.message || err.message || 'Could not start payment';
      if (/email/i.test(message)) {
        setEmailError(message);
        reveal('.membership-parent-container .email-field');
      } else {
        setPaymentError(message);
        reveal('.payment-error');
      }
      setActing('');
    }
  };

  const startMissedPayment = async (child = details, month) => {
    if (!child || !month?.year_month) {
      return;
    }
    const ids = childIds(child);
    try {
      setActing(`missed:${child.player_id}:${month.year_month}`);
      setEmailError(null);
      setPaymentError(null);
      if (!(await saveEmailIfNeeded(child))) {
        setActing('');
        reveal('.membership-parent-container .email-field');
        return;
      }
      const response = await api.post('/get_membership_status.php', {
        team_id: ids.team_id,
        player_id: ids.player_id,
        start_missed_checkout: 1,
        year_month: month.year_month,
        app_origin: window.location.origin,
      });
      if (response.data?.checkout_url) {
        window.location.href = response.data.checkout_url;
        return;
      }
      throw new Error('Could not start missed months payment');
    } catch (err) {
      const message = err.response?.data?.message || err.message || 'Could not start missed months payment';
      if (/email/i.test(message)) {
        setEmailError(message);
        reveal('.membership-parent-container .email-field');
      } else {
        setPaymentError(message);
        reveal('.payment-error');
      }
      setActing('');
    }
  };

  const openPortal = async (child = details) => {
    if (!child) {
      return;
    }
    const ids = childIds(child);
    try {
      setActing(`portal:${child.player_id}`);
      setPortalError(null);
      setErrorPlayerId(child.player_id);
      const response = await api.post('/get_membership_status.php', {
        team_id: ids.team_id,
        player_id: ids.player_id,
        open_portal: 1,
      });
      if (response.data?.portal_url) {
        window.location.href = response.data.portal_url;
        return;
      }
      throw new Error('Could not open subscription');
    } catch (err) {
      setPortalError(err.response?.data?.message || err.message || 'Could not open subscription');
      setErrorPlayerId(child.player_id);
      setActing('');
    }
  };

  const reinstateSubscription = async (child = details) => {
    if (!child) {
      return;
    }
    const ids = childIds(child);
    try {
      setActing(`reinstate:${child.player_id}`);
      setReinstateError(null);
      setErrorPlayerId(child.player_id);
      const response = await api.post('/get_membership_status.php', {
        team_id: ids.team_id,
        player_id: ids.player_id,
        reinstate_subscription: 1,
      });
      if (!response.data?.success) {
        throw new Error(response.data?.message || 'Could not reinstate this subscription');
      }
      await loadStatus({ quiet: true });
    } catch (err) {
      setReinstateError(
        err.response?.data?.message || err.message || 'Could not reinstate this subscription'
      );
      setErrorPlayerId(child.player_id);
    } finally {
      setActing('');
    }
  };

  return (
    <div className="membership-parent-container container">
      <Header />
      <h1 className="page-title">{isLoading ? 'Monthly Subscription' : pageTitle}</h1>

      {!isLoading && !error && details && !stacked ? (
        <div className="membership-notices">
          {details.subscribed && !cancelling ? (
            <div className="membership-notice is-success" role="status">
              <strong>Subscription completed</strong>
              <p>
                {confirmingPayment
                  ? 'You are subscribed. We are confirming this with Stripe.'
                  : 'You are subscribed to the monthly fee.'}
              </p>
            </div>
          ) : confirmingPayment ? (
            <div className="membership-notice is-success" role="status">
              <strong>Subscription completed</strong>
              <p>You are subscribed. We are confirming this with Stripe.</p>
            </div>
          ) : null}
          {cancelling ? (
            <div className="membership-notice is-warning" role="status">
              <strong>Subscription cancelled</strong>
              <p>
                {details.ends_on_date
                  ? `It stays in place until ${details.ends_on_date}. No further payments will be taken unless you reinstate it.`
                  : 'No further payments will be taken unless you reinstate it.'}
              </p>
            </div>
          ) : cancelled ? (
            <div className="membership-notice is-warning" role="status">
              <strong>Subscription cancelled</strong>
              <p>This subscription has ended. Reinstate it below to start a new monthly subscription for this season.</p>
            </div>
          ) : null}
          {canPayMissed ? (
            <div className="membership-notice is-warning" role="status">
              <strong>Missed payments</strong>
              <p>
                {missedMonths.length === 1
                  ? `${missedMonths[0].name} is unpaid. Pay this separately below.`
                  : `${missedMonths.length} previous months are unpaid. Pay these separately below.`}
              </p>
            </div>
          ) : null}
          {isFree ? (
            <div className="membership-notice is-success" role="status">
              <strong>Free</strong>
              <p>
                {details.subscribed && !cancelling
                  ? 'The club has marked this player as free. Cancel the subscription below if payments should stop.'
                  : 'This player does not need to pay a monthly fee.'}
              </p>
            </div>
          ) : null}
        </div>
      ) : null}

      {isLoading && <div className="loading">Loading subscription…</div>}
      {!isLoading && error && <div className="error">{error}</div>}

      {!isLoading && !error && stacked ? (
        <>
          <section className="membership-parent-card">
            {details.logo_url ? (
              <div className="logo-wrap">
                <img
                  className="team-logo"
                  src={details.logo_url}
                  alt={details.event_name || 'Team logo'}
                  onError={(event) => {
                    event.currentTarget.closest('.logo-wrap').style.display = 'none';
                  }}
                />
              </div>
            ) : null}
            <div className="pill-row">
              <span className="pill pill-sub">{seasonLabel}</span>
            </div>
            <h2>{details.child_name || pageTitle}</h2>
            {details.event_name ? <p className="lead">{details.event_name}</p> : null}
            {seasonDescription ? <p className="lead">{seasonDescription}</p> : null}
            <div className="rows">
              {cards.map((child) => {
                const amounts = childAmounts(child);
                const offer = siblingOffer(child);
                const playerKey = String(child.player_id || '');
                const cancellingChild = Boolean(child.cancel_at_period_end);
                const cancelledChild = ['cancelled', 'canceled', 'failed'].includes(child.subscription_status)
                  && !child.subscribed;
                const justPaidChild = returnStatus === 'success'
                  && String(child.player_id) === String(details.player_id);
                const confirmingChild = justPaidChild && !child.subscribed && !cancellingChild && !cancelledChild;
                const price = amounts.isFree
                  ? 'Free'
                  : gbp(monthlyPence(amounts.amountLabel));
                const stripeActive = Boolean(child.subscribed) && !cancellingChild;
                let status = '';
                if (cancellingChild) {
                  status = child.ends_on_date ? `Cancelled · ends ${child.ends_on_date}` : 'Cancelled';
                } else if (amounts.isFree) {
                  status = 'Free';
                } else if (stripeActive) {
                  status = 'Stripe subscription active';
                } else if (confirmingChild) {
                  status = 'Confirming with Stripe';
                } else {
                  status = 'No Stripe subscription';
                }
                const showPay = child.setup_ready !== false
                  && !amounts.isFree
                  && !child.subscribed
                  && !justPaidChild
                  && !confirmingChild;
                return (
                  <div className="row child-line" key={child.player_id || child.child_name}>
                    <div className="child-copy">
                      <div className="child-name-line">
                        <strong className="child-name">{child.child_name || 'Player'}</strong>
                        <span className={`pill ${amounts.isFree || stripeActive || confirmingChild ? 'pill-success' : cancellingChild ? 'pill-cancelled' : 'pill-muted'}`}>
                          {amounts.isFree ? 'Free' : cancellingChild ? 'Cancelled' : stripeActive || confirmingChild ? 'Subscribed' : 'Not subscribed'}
                        </span>
                      </div>
                      {status ? <span className="child-status">{status}</span> : null}
                      {child.last_payment_label ? (
                        <span className="child-status">{`Last payment ${child.last_payment_label}`}</span>
                      ) : stripeActive && child.subscription_started ? (
                        <span className="child-status">{`Started ${child.subscription_started}`}</span>
                      ) : null}
                      {child.setup_ready !== false && cancellingChild && !amounts.isFree ? (
                        <button
                          type="button"
                          className="row-action"
                          onClick={() => reinstateSubscription(child)}
                          disabled={Boolean(acting)}
                        >
                          {acting === `reinstate:${playerKey}` ? 'Reinstating…' : 'Reinstate'}
                        </button>
                      ) : null}
                      {showPay ? (
                        <button
                          type="button"
                          className="pay-button"
                          onClick={() => startPayment(child)}
                          disabled={Boolean(acting)}
                        >
                          {acting === `pay:${playerKey}` ? 'Continuing…' : 'Pay'}
                        </button>
                      ) : null}
                      {child.setup_ready !== false && child.subscribed && !cancellingChild && child.can_manage_subscription ? (
                        <button
                          type="button"
                          className="row-action"
                          onClick={() => openPortal(child)}
                          disabled={Boolean(acting)}
                        >
                          {acting === `portal:${playerKey}` ? 'Opening…' : `Cancel ${child.child_name || 'this child'} only`}
                        </button>
                      ) : null}
                      {String(errorPlayerId) === playerKey && (portalError || reinstateError) ? (
                        <p className="error-inline">{portalError || reinstateError}</p>
                      ) : null}
                    </div>
                    <div className="amount-col">
                      {offer ? (
                        <>
                          <div className="fee-line">
                            <span className="was-price">{offer.full}</span>
                            <strong>{offer.pay}</strong>
                          </div>
                          <span className="sibling-discount-line">{offer.caption}</span>
                        </>
                      ) : (
                        <div className="fee-line">
                          <strong>{price}</strong>
                        </div>
                      )}
                      {!offer && !child.fee_included && !amounts.isFree && (child.breakdown_label || (child.base_label && child.fee_label)) ? (
                        <button
                          type="button"
                          className="breakdown-button"
                          onClick={() => setFeeChild(child)}
                          aria-haspopup="dialog"
                        >
                          {child.breakdown_label
                            || `${child.base_label} + ${child.fee_label} fee`}
                          <FaInfoCircle aria-hidden="true" />
                        </button>
                      ) : null}
                    </div>
                  </div>
                );
              })}
              <div className="row amount">
                <span>Monthly total</span>
                <strong>
                  {`${gbp(cards.reduce((sum, child) => {
                    const amounts = childAmounts(child);
                    if (amounts.isFree && !child.subscribed) {
                      return sum;
                    }
                    return sum + monthlyPence(amounts.amountLabel);
                  }, 0))} per month`}
                </strong>
              </div>
            </div>
            {cards.some((child) => (
              child.setup_ready !== false
              && !(child.has_email && child.email)
              && (!child.subscribed || deriveMissedMonths(child).length > 0)
            )) ? (
              <div className="email-field">
                <label htmlFor="membership-email-family">Email address</label>
                <input
                  id="membership-email-family"
                  type="email"
                  autoComplete="email"
                  value={enteredEmail}
                  onChange={(event) => {
                    setEnteredEmail(event.target.value);
                    setEmailError(null);
                  }}
                  placeholder="you@example.com"
                  required
                />
                <p className="email-note">
                  This is saved to your account and used for receipts and cancel.
                </p>
                {emailError ? <p className="error-inline">{emailError}</p> : null}
              </div>
            ) : null}
            {paymentError ? <p className="error-inline payment-error">{paymentError}</p> : null}
          </section>
          {cards.some((child) => deriveMissedMonths(child).length > 0) ? (
            <section className="membership-parent-card missed-card">
              <h2>Previous months</h2>
              <p className="lead">
                This is a one-off payment. It is not part of the monthly subscription.
              </p>
              {paymentError ? <p className="error-inline payment-error">{paymentError}</p> : null}
              {cards.map((child) => {
                const missed = deriveMissedMonths(child);
                if (missed.length < 1) {
                  return null;
                }
                const playerKey = String(child.player_id || '');
                const freeChild = Boolean(child.is_free);
                const fallbackAmount = (childAmounts(child).amountLabel || '').replace(/\s+per month$/i, '');
                return (
                  <div className={`missed-child${freeChild ? ' is-free' : ''}`} key={`missed-${playerKey}`}>
                    <div className="rows">
                      <div className="row">
                        <span>Player</span>
                        <strong>
                          {child.child_name || 'Player'}
                          {freeChild ? <span className="membership-free">Free</span> : null}
                        </strong>
                      </div>
                      {missed.map((month) => (
                        <div className="row missed-month" key={month.year_month}>
                          <span>{month.name}</span>
                          <span className="missed-pay">
                            <strong>{freeChild ? 'Free' : (month.amount_label || fallbackAmount || 'Unpaid')}</strong>
                            {freeChild ? null : (
                              <button
                                type="button"
                                className="pay-button"
                                onClick={() => startMissedPayment(child, month)}
                                disabled={Boolean(acting)}
                              >
                                {acting === `missed:${playerKey}:${month.year_month}` ? 'Continuing…' : 'Pay'}
                              </button>
                            )}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </section>
          ) : null}
          {hasContact ? (
            <p className="family-intro">
              Need help?{' '}
              {hasContactEmail ? (
                <>
                  Email <a href={`mailto:${contact.email}`}>{contact.email}</a>
                </>
              ) : null}
              {hasContactEmail && hasContactPhone ? ' or ' : null}
              {hasContactPhone ? (
                <>
                  {hasContactEmail ? 'call' : 'Call'}{' '}
                  <a href={`tel:${getSignonPhoneHref(contact.phone)}`}>{contact.phone}</a>
                </>
              ) : null}
              {contact.name ? ` (${contact.name})` : ''}.
            </p>
          ) : null}
          <footer className="membership-parent-brand">
            <img src={logoLong} alt="FirstWhistle" />
          </footer>
        </>
      ) : null}

      {!isLoading && !error && details && !stacked && (
        <>
          <section className="membership-parent-card">
            {details.logo_url ? (
              <div className="logo-wrap">
                <img
                  className="team-logo"
                  src={details.logo_url}
                  alt={details.event_name || 'Team logo'}
                  onError={(event) => {
                    event.currentTarget.closest('.logo-wrap').style.display = 'none';
                  }}
                />
              </div>
            ) : null}

            {returnStatus === 'cancelled' ? (
              <>
                <span className="pill">Not completed</span>
                <h2>Payment cancelled</h2>
                <p className="lead">No payment was taken. You can try again when you are ready.</p>
              </>
            ) : (
              <>
                <div className="pill-row">
                  {cancelling || cancelled ? (
                    <span className="pill pill-cancelled">Cancelled</span>
                  ) : details.subscribed || confirmingPayment ? (
                    <span className="pill pill-success">Subscribed</span>
                  ) : isFree ? (
                    <span className="pill pill-success">Free</span>
                  ) : null}
                  <span className="pill pill-sub">{seasonLabel}</span>
                </div>
                <h2>{details.event_name || pageTitle}</h2>
                {setupReady ? (
                  seasonDescription ? <p className="lead">{seasonDescription}</p> : null
                ) : (
                  <p className="lead">{details.setup_note || 'Monthly subscription is not set up yet.'}</p>
                )}
              </>
            )}

            <div className="rows">
              {details.child_name ? (
                <div className="row">
                  <span>Player</span>
                  <strong>{details.child_name}</strong>
                </div>
              ) : null}
              {amountLabel ? (
                <div className="row amount">
                  <span>Amount</span>
                  <div className="amount-col">
                    {offer ? (
                      <>
                        <div className="fee-line">
                          <span className="was-price">{offer.full}</span>
                          <strong>{`${offer.pay} per month`}</strong>
                        </div>
                        <span className="sibling-discount-line">{offer.caption}</span>
                      </>
                    ) : (
                      <div className="fee-line">
                        <strong>{amountLabel}</strong>
                      </div>
                    )}
                    {!offer && !details.fee_included && !isFree && (details.breakdown_label || (details.base_label && details.fee_label)) ? (
                      <button
                        type="button"
                        className="breakdown-button"
                        onClick={() => setFeeChild(details)}
                        aria-haspopup="dialog"
                        aria-expanded={Boolean(feeChild)}
                      >
                        {details.breakdown_label
                          || `${details.base_label} + ${details.fee_label} fee`}
                        <FaInfoCircle aria-hidden="true" />
                      </button>
                    ) : null}
                  </div>
                </div>
              ) : null}
              {!isFree && details.subscribed && !cancelling ? (
                <div className="row">
                  <span>Subscription</span>
                  <strong>You are subscribed</strong>
                </div>
              ) : null}
              {cancelling ? (
                <div className="row">
                  <span>Subscription</span>
                  <strong>Cancelled</strong>
                </div>
              ) : null}
              {cancelling && details.ends_on_date ? (
                <div className="row">
                  <span>Ends on</span>
                  <strong>{details.ends_on_date}</strong>
                </div>
              ) : null}
            </div>

            {setupReady && cancelling && !isFree ? (
              <>
                <p className="status-note">
                  {details.status_note
                    || 'This subscription is cancelled. Reinstate it to keep monthly payments going.'}
                </p>
                <button
                  type="button"
                  className="cta"
                  onClick={() => reinstateSubscription()}
                  disabled={busy}
                >
                  {reinstating ? 'Reinstating…' : 'Reinstate subscription'}
                </button>
                <p className="status-note">
                  You will not be charged now. Monthly payments start again from the next payment date.
                </p>
                {reinstateError ? <p className="error-inline">{reinstateError}</p> : null}
              </>
            ) : setupReady && isFree ? (
              <>
                <p className="status-note">
                  {details.status_note || 'This player is free. No monthly fee is due.'}
                </p>
                {details.subscribed && !cancelling && details.can_manage_subscription ? (
                  <button
                    type="button"
                    className="cta"
                    onClick={() => openPortal()}
                    disabled={openingPortal || busy}
                  >
                    {openingPortal ? 'Opening…' : 'Cancel subscription'}
                  </button>
                ) : null}
              </>
            ) : setupReady && details.subscribed ? (
              <>
                <p className={`status-note${details.subscription_status === 'past_due' ? ' is-warning' : ''}`}>
                  {details.status_note}
                </p>
                {details.subscription_status === 'past_due' && details.can_manage_subscription ? (
                  <button
                    type="button"
                    className="cta"
                    onClick={() => openPortal()}
                    disabled={openingPortal || busy}
                  >
                    {openingPortal ? 'Opening…' : 'Update payment details'}
                  </button>
                ) : null}
              </>
            ) : setupReady ? (
              <>
                {['cancelled', 'canceled', 'failed'].includes(details.subscription_status) && details.status_note && !justPaid ? (
                  <p className="status-note">{details.status_note}</p>
                ) : confirmingPayment ? (
                  <p className="status-note">We are confirming your subscription with Stripe.</p>
                ) : null}
                {hasAccountEmail ? (
                  <p className="email-note">
                    Email on this account: <strong>{details.email}</strong>
                  </p>
                ) : missingAccountEmail ? (
                  <div className="email-field">
                    <label htmlFor="membership-email">Email address</label>
                    <input
                      id="membership-email"
                      type="email"
                      autoComplete="email"
                      value={enteredEmail}
                      onChange={(event) => {
                        setEnteredEmail(event.target.value);
                        setEmailError(null);
                      }}
                      placeholder="you@example.com"
                      required
                    />
                    <p className="email-note">
                      This is saved to your account and used for receipts and cancel.
                    </p>
                    {emailError ? <p className="error-inline">{emailError}</p> : null}
                  </div>
                ) : null}
                {!details.subscribed && !justPaid ? (
                  <button
                    type="button"
                    className="cta"
                    onClick={() => startPayment()}
                    disabled={busy}
                  >
                    {starting
                      ? 'Continuing…'
                      : cancelled
                        ? 'Reinstate subscription'
                        : 'Pay Subscription Fee'}
                  </button>
                ) : null}
                {paymentError ? <p className="error-inline payment-error">{paymentError}</p> : null}
              </>
            ) : (
              <p className="status-note">{details.setup_note}</p>
            )}
          </section>

          {setupReady && canPayMissed && !isFree && missedMonths.length > 0 ? (
            <section className="membership-parent-card missed-card">
              <h2>Previous months</h2>
              <p className="lead">
                These months were due before this subscription starts.
                Paying them is separate from the monthly fee.
              </p>
              {paymentError ? <p className="error-inline payment-error">{paymentError}</p> : null}
              <div className="rows">
                {missedMonths.map((month) => (
                  <div className="row missed-month" key={month.year_month}>
                    <span>{month.name}</span>
                    <span className="missed-pay">
                      <strong>{month.amount_label || 'Unpaid'}</strong>
                      <button
                        type="button"
                        className="pay-button"
                        onClick={() => startMissedPayment(details, month)}
                        disabled={busy}
                      >
                        {acting === `missed:${details.player_id}:${month.year_month}` ? 'Continuing…' : 'Pay'}
                      </button>
                    </span>
                  </div>
                ))}
              </div>
              {details.subscribed && missingAccountEmail ? (
                <div className="email-field">
                  <label htmlFor="membership-email-missed">Email address</label>
                  <input
                    id="membership-email-missed"
                    type="email"
                    autoComplete="email"
                    value={enteredEmail}
                    onChange={(event) => {
                      setEnteredEmail(event.target.value);
                      setEmailError(null);
                    }}
                    placeholder="you@example.com"
                    required
                  />
                  {emailError ? <p className="error-inline">{emailError}</p> : null}
                </div>
              ) : null}
            </section>
          ) : null}

          {setupReady ? (
            <>
          <section className={`membership-parent-accordion${detailsOpen ? ' is-open' : ''}`}>
            <button
              type="button"
              className="membership-parent-handle"
              onClick={() => setDetailsOpen((open) => !open)}
              aria-expanded={detailsOpen}
            >
              <span>When payment is taken</span>
              <FaChevronDown aria-hidden="true" />
            </button>
            {detailsOpen && (
              <div className="membership-parent-panel">
                {cancelling && details.ends_on_date ? (
                  <p className="status-note">
                    Ends on {details.ends_on_date}. No further payments will be taken.
                  </p>
                ) : null}
                {details.show_payment_due && details.payment_due_label ? (
                  <p className="due-note">{details.payment_due_label}</p>
                ) : isFree && !details.subscribed ? (
                  <p className="status-note">
                    This player is free for {seasonLabel}. No monthly fee is taken.
                  </p>
                ) : cancelling || cancelled ? (
                  <p className="status-note">
                    No further monthly payments will be taken for {seasonLabel}.
                  </p>
                ) : (
                  <p className="status-note">
                    This is a monthly subscription for {seasonLabel}.
                    Payment is taken automatically each month until the season ends.
                  </p>
                )}
              </div>
            )}
          </section>

          {details.subscribed && !cancelling ? (
          <section className={`membership-parent-accordion${cancelOpen ? ' is-open' : ''}`}>
            <button
              type="button"
              className="membership-parent-handle"
              onClick={() => setCancelOpen((open) => !open)}
              aria-expanded={cancelOpen}
            >
              <span>How to cancel</span>
              <FaChevronDown aria-hidden="true" />
            </button>
            {cancelOpen && (
              <div className="membership-parent-panel">
                <p className="status-note">
                  This cancels only {details.child_name || 'this child'}. Another child’s subscription is left as it is.
                </p>
                {details.can_manage_subscription ? (
                  <button
                    type="button"
                    className="cancel-text-button"
                    onClick={() => openPortal()}
                    disabled={openingPortal}
                  >
                    {openingPortal ? 'Opening…' : `Cancel ${details.child_name || 'this child'} only`}
                  </button>
                ) : details.subscribed ? (
                  <p className="status-note">
                    Open the Stripe receipt email sent when you subscribed, then choose Manage subscription.
                  </p>
                ) : (
                  <p className="status-note">
                    After you subscribe, you can cancel from this page.
                  </p>
                )}
                {portalError ? <p className="error-inline">{portalError}</p> : null}
                {details.can_manage_subscription || details.subscribed ? (
                  <p className="status-note">
                    Future monthly payments will stop. Payments already taken for this season stay paid.
                  </p>
                ) : null}
                {hasContact ? (
                  <p className="status-note">
                    Need help?{' '}
                    {hasContactEmail ? (
                      <>
                        Email <a href={`mailto:${contact.email}`}>{contact.email}</a>
                      </>
                    ) : null}
                    {hasContactEmail && hasContactPhone ? ' or ' : null}
                    {hasContactPhone ? (
                      <>
                        {hasContactEmail ? 'call' : 'Call'}{' '}
                        <a href={`tel:${getSignonPhoneHref(contact.phone)}`}>{contact.phone}</a>
                      </>
                    ) : null}
                    {contact.name ? ` (${contact.name})` : ''}.
                  </p>
                ) : null}
              </div>
            )}
          </section>
          ) : null}
            </>
          ) : null}

          <footer className="membership-parent-brand">
            <img src={logoLong} alt="FirstWhistle" />
          </footer>
        </>
      )}

      {feeChild ? (
        <MembershipFeeModal
          open
          onClose={() => setFeeChild(null)}
          clubLabel={feeChild.club_name ? `${feeChild.club_name} fee` : 'Club fee'}
          clubAmount={
            childAmounts(feeChild).discountApplied
              ? (feeChild.list_club_label || feeChild.sibling_discount?.club_label)
              : feeChild.base_label
          }
          discountLabel={childAmounts(feeChild).discountTitle}
          discountAmount={
            childAmounts(feeChild).discountApplied
              ? `−${feeChild.discount_label || feeChild.sibling_discount?.discount_label}`
              : ''
          }
          feeAmount={feeChild.fee_label}
          totalAmount={childAmounts(feeChild).amountLabel}
          totalLabel="You pay"
        />
      ) : null}
    </div>
  );
};

export default MembershipParent;
